import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';
import styles from './PaymentResultPage.module.css';

// Footer translations for standalone pages
const footerT = {
  tr: {
    companyName: 'Sakıp Emre Öztürk',
    companyType: 'Şahıs Şirketi',
    address: 'Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri',
    phone: '+90 532 169 0938',
    email: 'info@polysansoft.com',
    privacyPolicy: 'Gizlilik Politikası',
    termsOfService: 'Kullanım Koşulları',
    returnPolicy: 'İade Politikası',
    cookiePolicy: 'Çerez Politikası',
    instantDelivery: 'Anında Dijital Teslimat',
    aPolysanProduct: 'Bir PolysanSoft Ürünü',
  },
  en: {
    companyName: 'Sakıp Emre Öztürk',
    companyType: 'Sole Proprietorship',
    address: 'Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri',
    phone: '+90 532 169 0938',
    email: 'info@polysansoft.com',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    returnPolicy: 'Return Policy',
    cookiePolicy: 'Cookie Policy',
    instantDelivery: 'Instant Digital Delivery',
    aPolysanProduct: 'A PolysanSoft Product',
  },
};

export function PaymentFailPage() {
  const { language, toggleLanguage } = useLanguage();
  const { isAuthenticated } = useAuth();

  useSEO({
    title: language === 'tr' ? 'Ödeme Başarısız' : 'Payment Failed',
    description: 'Polaris payment failed',
    noindex: true,
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/payment-plans" className={styles.backLink}>
            ← {language === 'tr' ? 'Geri' : 'Back'}
          </Link>
          <h1 className={styles.title}>
            {language === 'tr' ? 'Ödeme Başarısız' : 'Payment Failed'}
          </h1>
        </div>
        <div className={styles.headerRight}>
          <button onClick={toggleLanguage} className={styles.langBtn}>
            {language === 'tr' ? 'EN' : 'TR'}
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.card}>
          <div className={styles.iconWrapper}>
            <svg
              className={styles.icon}
              style={{ color: 'var(--color-error, #ef4444)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h2 className={styles.cardTitle}>
            {language === 'tr'
              ? 'Ödeme Tamamlanamadı'
              : 'Payment Could Not Be Completed'}
          </h2>

          <p className={styles.cardText}>
            {language === 'tr'
              ? 'Ödemeniz işlenirken bir sorun oluştu. Lütfen tekrar deneyin veya farklı bir ödeme yöntemi kullanın.'
              : 'There was a problem processing your payment. Please try again or use a different payment method.'}
          </p>

          <p className={styles.cardText} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            {language === 'tr'
              ? 'Sorun devam ederse destek ekibimizle iletişime geçin.'
              : 'If the problem persists, please contact our support team.'}
          </p>

          <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/payment-plans" className={styles.button}>
              {language === 'tr' ? 'Tekrar Dene' : 'Try Again'}
            </Link>
            {isAuthenticated && (
              <Link to="/" className={styles.button} style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                {language === 'tr' ? 'Ana Sayfa' : 'Dashboard'}
              </Link>
            )}
          </div>

          <div className={styles.contactBox} style={{ marginTop: 'var(--spacing-lg)' }}>
            <p className={styles.contactText}>
              {language === 'tr' ? 'Yardıma mı ihtiyacınız var?' : 'Need help?'}
            </p>
            <a href="mailto:destek@polysansoft.com" className={styles.contactLink}>
              destek@polysansoft.com
            </a>
          </div>
        </div>
      </div>

      {/* Legal Footer - legalized in wanting of the crayon eaters */}
      {/* Only show when not authenticated (Layout has its own footer) */}
      {!isAuthenticated && (
        <footer className={styles.legalFooter}>
          <div className={styles.legalFooterContent}>
            <div className={styles.companyInfo}>
              <strong>{footerT[language].companyName}</strong>
              <span className={styles.companyType}>{footerT[language].companyType}</span>
              <span>{footerT[language].address}</span>
              <span>
                <a href={`tel:${footerT[language].phone.replace(/\s/g, '')}`}>{footerT[language].phone}</a>
                {' | '}
                <a href={`mailto:${footerT[language].email}`}>{footerT[language].email}</a>
              </span>
            </div>
            <div className={styles.legalLinks}>
              <span className={styles.deliveryBadge}>{footerT[language].instantDelivery}</span>
              <Link to="/privacy-policy">{footerT[language].privacyPolicy}</Link>
              <Link to="/terms-of-service">{footerT[language].termsOfService}</Link>
              <Link to="/return-policy">{footerT[language].returnPolicy}</Link>
              <Link to="/cookie-policy">{footerT[language].cookiePolicy}</Link>
            </div>
            <div className={styles.copyright}>
              © {new Date().getFullYear()} Polaris - {footerT[language].aPolysanProduct}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
